import { AlertCircle } from "lucide-react";
import { Control, FieldErrors } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useState, useEffect } from "react";
import { ContactFormSchema } from "./contactFormSchema";
import {
  HEALTHMATCH_PRIVACY_URL,
  HEALTHMATCH_TERMS_URL,
} from "../../utils/urlConfig";

interface TermsAcceptanceProps {
  countryCode: string;
  control: Control<ContactFormSchema>;
  errors: FieldErrors<ContactFormSchema>;
  handleInputFocus: (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLButtonElement
    >,
  ) => void;
}

export function TermsAcceptance({
  countryCode,
  control,
  errors,
  handleInputFocus,
}: TermsAcceptanceProps) {
  // For Canadian users, we need separate checkboxes with local state
  const isSplitTermsRequired = countryCode === "CA";

  // Local state for CA users
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [privacyError, setPrivacyError] = useState("");

  // Clear errors when checkboxes are checked
  useEffect(() => {
    if (termsChecked) setTermsError("");
    if (privacyChecked) setPrivacyError("");
  }, [termsChecked, privacyChecked]);

  // Set specific errors when form validation fails
  useEffect(() => {
    if (errors.termsAccepted && isSplitTermsRequired) {
      if (!termsChecked)
        setTermsError("You must accept the Terms and Conditions");
      if (!privacyChecked)
        setPrivacyError("You must accept the Privacy Policy");
    }
  }, [
    errors.termsAccepted,
    termsChecked,
    privacyChecked,
    isSplitTermsRequired,
  ]);

  // For Canadian users: show split terms and privacy checkboxes
  if (isSplitTermsRequired) {
    return (
      <div className="space-y-6">
        {/* Terms and Conditions */}
        <div className="relative flex items-start p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group">
          <div className="flex h-5 items-center">
            <Controller
              name="termsAccepted"
              control={control}
              render={({ field }) => (
                <input
                  id="termsConditionsAccepted"
                  data-test-id="input-termsConditionsAccepted"
                  type="checkbox"
                  checked={termsChecked}
                  onChange={(e) => {
                    setTermsChecked(e.target.checked);
                    // Only set the main field to true if both are checked
                    field.onChange(e.target.checked && privacyChecked);
                  }}
                  onFocus={handleInputFocus}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200"
                />
              )}
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label
              htmlFor="termsConditionsAccepted"
              className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200"
            >
              I agree to the HealthMatch{" "}
              <a
                href={HEALTHMATCH_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-500"
              >
                Terms and Conditions
              </a>
            </label>
            {termsError && (
              <div className="text-red-500 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {termsError}
              </div>
            )}
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="relative flex items-start p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group">
          <div className="flex h-5 items-center">
            <Controller
              name="termsAccepted"
              control={control}
              render={({ field }) => (
                <input
                  id="privacyPolicyAccepted"
                  data-test-id="input-privacyPolicyAccepted"
                  type="checkbox"
                  checked={privacyChecked}
                  onChange={(e) => {
                    setPrivacyChecked(e.target.checked);
                    // Only set the main field to true if both are checked
                    field.onChange(e.target.checked && termsChecked);
                  }}
                  onFocus={handleInputFocus}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200"
                />
              )}
            />
          </div>
          <div className="ml-3 text-sm leading-6">
            <label
              htmlFor="privacyPolicyAccepted"
              className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200"
            >
              I agree to the HealthMatch{" "}
              <a
                href={HEALTHMATCH_PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-500"
              >
                Privacy Policy
              </a>
            </label>
            {privacyError && (
              <div className="text-red-500 text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {privacyError}
              </div>
            )}
          </div>
        </div>

        {/* Show form error if present */}
        {errors.termsAccepted && errors.termsAccepted.message && (
          <div className="text-red-500 text-sm mt-1 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            <span>
              Please accept both Terms and Conditions and Privacy Policy
            </span>
          </div>
        )}
      </div>
    );
  }

  // For US and other users: show the original combined checkbox
  return (
    <div className="relative flex items-start p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group">
      <div className="flex h-5 items-center">
        <Controller
          name="termsAccepted"
          control={control}
          render={({ field }) => (
            <input
              id="termsAccepted"
              data-test-id="input-termsAccepted"
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              onFocus={handleInputFocus}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors duration-200"
            />
          )}
        />
      </div>
      <div className="ml-3 text-sm leading-6">
        <label
          htmlFor="termsAccepted"
          className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200"
        >
          I agree to the HealthMatch{" "}
          <a
            href={HEALTHMATCH_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-500"
          >
            Terms and Conditions
          </a>{" "}
          and{" "}
          <a
            href={HEALTHMATCH_PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-500"
          >
            Privacy Policy
          </a>
        </label>
        {errors.termsAccepted && (
          <div className="text-red-500 text-sm mt-1 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {errors.termsAccepted.message}
          </div>
        )}
      </div>
    </div>
  );
}
